/**
 * **ONDE ESTA ÁREA MORA** — o endereço, que agora depende do host.
 *
 * ## Os dois modos, e o que os separa
 *
 * | modo | quem serve | a vitrine abre em |
 * |---|---|---|
 * | painel | `<painel>/aluno/` | `/aluno/a/<slug>` |
 * | domínio do cliente | `curso.cliente.com.br/` | `/` |
 *
 * No segundo, o slug **sai da URL**: o host já diz qual área é, e repeti-lo no
 * caminho seria escrever duas vezes a mesma coisa (`curso.cliente.com.br/a/curso`).
 *
 * ## Por que a área vem do HTML, e não de um `fetch`
 *
 * Porque o roteador é montado no primeiro render. Buscá-la por rede faria a
 * aplicação nascer sem saber para onde mandar `/` — uma tela em branco piscando
 * em todo carregamento, e um estado «carregando» dentro de `main.tsx`. O
 * servidor já sabe a resposta quando monta o documento (`lib/servir-area.ts`) e
 * a injeta num `<script>` de uma linha.
 *
 * ## O que NÃO muda de forma, e é metade do desenho
 *
 * **Os caminhos de API.** `api.get('/a/<slug>/meu-progresso')` continua igual nos
 * dois modos, e `BASE` continua `'/aluno/api'` — relativa, portanto same-origin
 * no host que serviu o bundle, portanto sem CORS. O que este arquivo governa é
 * só o que aparece na barra de endereço.
 *
 * Misturar os dois seria o defeito mais caro possível aqui: um `caminhoDaArea`
 * aplicado por engano a uma chamada de API mandaria o aluno pedir
 * `/aluno/api/meu-progresso` sem área nenhuma, e a resposta seria um 404 que não
 * aponta para lugar nenhum.
 */

declare global {
  interface Window {
    __VESTIGO_AREA__?: { slug: string };
  }
}

/**
 * O slug da área deste host, ou `null` quando o bundle é servido pelo painel.
 *
 * Lido uma vez, no carregamento do módulo: o host não muda no meio de uma
 * sessão, e reler a cada render convidaria alguém a mudá-lo em teste e a ver a
 * aplicação divergir do roteador já montado.
 */
export const AREA_DO_HOST: string | null =
  typeof window !== 'undefined' && typeof window.__VESTIGO_AREA__?.slug === 'string'
    ? window.__VESTIGO_AREA__.slug
    : null;

/**
 * O `basename` do roteador.
 *
 * No painel a SPA é servida em `/aluno/` pela mesma API que serve o painel — sem
 * o basename, o roteador montaria rotas na raiz e o carve-out do `spaPlugin`
 * devolveria o index do painel na primeira navegação recarregada. No host da
 * área ela É a raiz, e um basename ali quebraria toda rota.
 */
export const BASENAME = AREA_DO_HOST ? '' : '/aluno';

/**
 * O padrão de rota, para `<Route path=…>`.
 *
 * `rotaDaArea('/aula/:aulaId')` devolve `/aula/:aulaId` no host da área e
 * `/a/:areaSlug/aula/:aulaId` no painel. A vitrine é `rotaDaArea('')`, que vira
 * `/` num modo e `/a/:areaSlug` no outro.
 */
export function rotaDaArea(resto: string): string {
  if (AREA_DO_HOST) return resto === '' ? '/' : resto;
  return `/a/:areaSlug${resto}`;
}

/**
 * O slug da área desta tela — do caminho, ou do HOST quando o caminho não o tem.
 *
 * ## O defeito que ela existe para não deixar acontecer
 *
 * No host da área o padrão de rota deixou de ter `:areaSlug`, então
 * `useParams().areaSlug` é `undefined` ali. Oito telas faziam
 * `const { areaSlug = '' } = useParams()`, e o `= ''` — que existia para o
 * TypeScript, não para o produto — transformaria a ausência em **string vazia**:
 * a chamada seguinte seria `api.get('/a//meu-progresso')`, um 404 que não aponta
 * para lugar nenhum e que só aparece no domínio do cliente.
 *
 * É o modo de falhar mais caro desta obra: silencioso, ausente no host do painel
 * onde se desenvolve, e presente exatamente onde o aluno está. Daí ela ser um
 * hook e não uma linha repetida oito vezes.
 */
export function useAreaSlug(doCaminho: string | undefined): string {
  return doCaminho ?? AREA_DO_HOST ?? '';
}

/**
 * O slug da área nas TRÊS TELAS DE ENTRADA — as que não têm `:areaSlug` na rota.
 *
 * ## Por que ela é diferente de `useAreaSlug`, que está logo acima
 *
 * Porque `/entrar`, `/primeiro-acesso` e `/esqueci-senha` não moram dentro da
 * área: elas são rotas de topo, e levam a área na BUSCA (`?a=<slug>`) porque
 * navegam umas para as outras e a busca atravessa os `<Link>`. `useParams` não
 * tem nada para lhes dar, e é por isso que `useAreaSlug` não serve aqui.
 *
 * ## Por que uma função, e não a linha repetida
 *
 * Ela estava escrita duas vezes — em `entrada/Moldura.tsx`, para buscar a cara,
 * e em `App.tsx`, para montar o endereço do cadastro. Duas cópias da pergunta
 * «de que área é esta tela de entrar?» é o género de divergência que aparece
 * como *a tela veste a área certa e o link leva à errada*, e nenhuma das duas
 * parece defeituosa sozinha.
 *
 * `''` nunca sai daqui: quem não tem área é `null`, porque a ausência tem de
 * poder ser testada. Uma cadeia vazia viraria `/entrada/` na chamada seguinte —
 * o defeito que `useAreaSlug` documenta no cabeçalho dela.
 */
export function slugDaEntrada(): string | null {
  return new URLSearchParams(window.location.search).get('a') ?? AREA_DO_HOST;
}

/**
 * ── AS ROTAS DE TOPO, QUE NÃO SÃO DE ÁREA NENHUMA ───────────────────────────
 *
 * Elas existem nos dois modos com o MESMO caminho, porque são anteriores a
 * escolher uma área — ou, no caso da prévia, porque não há sessão nelas.
 * `/cadastro` não está aqui e é tratado à parte: no host do painel ele vive
 * DENTRO de `/a/<slug>/`, e por isso não é um caminho de topo.
 */
const ROTAS_FORA_DA_AREA = ['/entrar', '/primeiro-acesso', '/esqueci-senha', '/previa'];

/**
 * ── AS DUAS TELAS PÚBLICAS DE UMA ÁREA, NUMA RÉGUA SÓ ───────────────────────
 *
 * `/cadastro` e `/oferta/<curso>` no host da área; `/a/<slug>/cadastro` e
 * `/a/<slug>/oferta/<curso>` no do painel.
 *
 * As duas estão aqui pela MESMA razão: quem as lê **ainda não tem matrícula**, e
 * o `PortaoDosTermos` existe para quem tem. A primeira entrou em 20/09 (`CE3`); a
 * segunda, em 02/10 (`0461`), caiu no mesmo buraco por ser uma rota que não
 * existia quando aquele conserto foi escrito. Sem ela, a página pública dispara
 * `GET /a/<slug>/termos` a cada visita, leva `401` e engole.
 *
 * Um pedido que leva 401 e é engolido não quebra a tela; o que ele faz é pior de
 * achar: enche o log de uma rota pública com 401, paga uma ida ao servidor em
 * cada visita vinda de um anúncio, e ensina quem for ler o log que 401 ali é
 * normal.
 *
 * ## UMA alternância, e não duas constantes — e o motivo é um NÚMERO
 *
 * O pedaço de entrada deste app tem teto de **335.000 B** e, em 02/10/2026,
 * estava em **334.966**: trinta e quatro bytes de folga. Duas constantes e dois
 * `.test()` em dois sítios custam o dobro de uma alternância e um `.test()`, e
 * aqui isso não é estética. Ver `comunidade/o-editor-nao-entra-na-porta.test.ts`.
 *
 * **Âncora, e o segmento do curso é obrigatório.** Um post do mural cujo endereço
 * acabe em «cadastro» ou em «oferta» continua vigiado, e `/oferta` pelado — que
 * não é rota nenhuma, e que o roteador manda para a vitrine — também.
 */
const E_TELA_PUBLICA = /^\/(cadastro|oferta\/[^/]+)\/?$/;

/**
 * ── DE QUE ÁREA É ESTE CAMINHO? — e `null` quando não é de nenhuma ──────────
 *
 * Quem pergunta é o portão dos termos (`componentes/PortaoDosTermos.tsx`), que
 * vive FORA das rotas e portanto não tem `useParams` para consultar: ele só tem
 * o `pathname`.
 *
 * ## O defeito que ela fecha (`CE4`)
 *
 * O portão tinha esta régua escrita à mão, e era `/^\/a\/([^/]+)/` — só isso.
 * **No domínio próprio de um cliente o caminho não tem `/a/<slug>`**: a vitrine
 * é `/`, a aula é `/aula/<id>`. Então a régua nunca casava, o portão nunca
 * corria, e quem não tinha aceitado os termos levava o **403 seco do servidor**
 * em vez da janela de aceite. Silencioso e presente exactamente onde o aluno
 * está — o modo de falhar mais caro desta casa, o mesmo que `useAreaSlug`
 * descreve no cabeçalho dela.
 *
 * ## Por que o host próprio é uma LISTA DE EXCEÇÕES, e não uma de inclusões
 *
 * Porque ali o host já escolheu a área, e a pergunta deixa de ser «qual área?»
 * para ser «esta tela está dentro dela?». A resposta honesta é *quase sempre
 * sim* — a tabela de rotas tem quatro telas de topo e todo o resto é da área.
 *
 * Uma lista de inclusões tornaria o portão **omisso por omissão**: a rota nova
 * que alguém acrescentar amanhã nasceria sem portão, e o sintoma seria o 403
 * seco outra vez, só naquela tela. Por exceções ela nasce COM portão, e o pior
 * desfecho de um erro meu é um pedido a mais que leva 401 e é engolido — que é
 * ruído, não porta aberta. Das duas direções de errar, esta é a barata.
 *
 * ## O que ela NÃO é
 *
 * Não é a tranca. A tranca é o 403 do servidor em toda rota de área, e continua
 * a ser — isto decide só onde a JANELA de aceite pode aparecer.
 */
export function slugDaAreaNoCaminho(caminho: string): string | null {
  /* A barra final não muda a tela, e o React Router casa as duas formas. */
  const limpo = caminho.length > 1 ? caminho.replace(/\/$/, '') : caminho;

  if (AREA_DO_HOST) {
    if (ROTAS_FORA_DA_AREA.includes(limpo) || E_TELA_PUBLICA.test(caminho)) return null;
    return AREA_DO_HOST;
  }

  const m = /^\/a\/([^/]+)(\/.*)?$/.exec(caminho);
  if (!m) return null;
  /* As DUAS telas públicas desta área não são vigiadas: quem está nelas ainda
     não tem matrícula, e o portão existe para quem tem. Âncora e não sufixo —
     um post do mural cujo endereço acabe em «cadastro» continua vigiado. */
  if (E_TELA_PUBLICA.test(m[2] ?? '')) return null;
  return decodeURIComponent(m[1]!);
}

/**
 * O caminho concreto, para `<Link to=…>` e `navigate(…)`.
 *
 * `slug` continua a ser exigido mesmo no host da área — e não é cerimónia: quem
 * chama tem o slug na mão de qualquer forma, e exigi-lo faz o mesmo componente
 * servir os dois modos sem saber em qual está. Ignorá-lo aqui é a única linha
 * que precisa saber.
 */
export function caminhoDaArea(slug: string, resto = ''): string {
  if (AREA_DO_HOST) return resto === '' ? '/' : resto;
  return `/a/${slug}${resto}`;
}

/**
 * ── A VOLTA PARA A AULA, E PARA A ABA DE ONDE SE SAIU (`AUL`) ───────────────
 *
 * A ordem do dono: *«quando ele vai para o mural, deve poder voltar para a aula
 * e aba de comentário de onde ele originou o click»*.
 *
 * **A volta é um ENDEREÇO, e não um estado guardado em memória.** Um estado não
 * sobrevive ao recarregar, não sobrevive ao botão de voltar do navegador e não
 * se cola a ninguém — e a pessoa que perde o sítio duas vezes deixa de clicar.
 *
 * Ela mora aqui pela mesma razão que `caminhoDaArea` mora aqui, e não é
 * arrumação: quem ESCREVE este endereço é a página do post, no mural; quem o LÊ
 * é o painel da aula, do outro lado do produto. Duas literais em dois arquivos
 * é a ligação que se parte em silêncio no dia em que alguém renomear uma
 * delas, e o sintoma seria a volta funcionar e abrir na aba errada.
 */
export const ABA_DOS_COMENTARIOS = 'comentarios';

/** `/a/<area>/aula/<id>?aba=comentarios` — a aula, aberta na conversa. */
export function caminhoDaConversaDaAula(slug: string, aulaId: string): string {
  return `${caminhoDaArea(slug, `/aula/${aulaId}`)}?aba=${ABA_DOS_COMENTARIOS}`;
}

/**
 * O slug da área a partir do CAMINHO, para quem está **fora** da tabela de
 * rotas.
 *
 * ## Por que ela existe ao lado de `useAreaSlug`, e não em vez dela
 *
 * `useAreaSlug` lê `useParams()`, e `useParams()` só tem valor **dentro** de
 * uma `<Route>`. O provedor de idioma envolve a tabela inteira — ele precisa
 * de saber a área antes de qualquer rota casar —, e ali `useParams()`
 * devolveria `{}` sempre: no host próprio cairia no `AREA_DO_HOST` por sorte,
 * e no painel ficaria vazio, que é o modo de falhar silencioso que o
 * cabeçalho de `useAreaSlug` descreve.
 *
 * A precedência é a mesma das outras duas (host primeiro), e é de propósito:
 * no domínio do cliente não há `/a/<slug>` no caminho para ler.
 *
 * O `pathname` vem **já sem o `BASENAME`** — é o que o `useLocation` do
 * roteador entrega —, e por isso o padrão é `/a/<slug>` e nunca
 * `/aluno/a/<slug>`.
 */
export function slugDoCaminho(pathname: string): string {
  if (AREA_DO_HOST) return AREA_DO_HOST;
  const m = /^\/a\/([^/?#]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : '';
}
